import { AlertCircle, CheckCircle2, Clock, Flame, Package } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { C } from "./homeTheme";
import { FORT_AGENT_STATUS, MyFortAgentItem } from "@/api/types";
import {
  formatEtherWithDecimals,
  formatThousands,
  formatUsdPrefix,
} from "@/lib/utils";
import { getAgentTierKey, getTokenName } from "@/config/common";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { AGENT_IMAGE_URL } from "@/components/home/MyAgentsSection/constants";
import { CCC_AIF_ADDRESS, CCC_FUSD_ADDRESS, USDT_ADDRESS } from "@/config/env";

export function MyAgentInactiveDetailDialog({
  agent,
  open,
  onClose,
}: {
  agent: MyFortAgentItem | null;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  if (!agent || agent.status === FORT_AGENT_STATUS.ACTIVATED) return null;

  const mustPayFusdBeforeActivate =
    agent.paymentMode === "mixed" &&
    !agent.fusdPurchaseDeducted &&
    BigInt(agent.purchasePayFusdWei || "0") > 0n;

  /** 混合购且存在应付 FUSD 时，在购买信息区展示补缴状态（待补缴 / 已补缴） */
  const hasFusdInPurchase =
    agent.paymentMode === "mixed" &&
    BigInt(agent.purchasePayFusdWei || "0") > 0n;

  const tierLabel = t(getAgentTierKey(agent.name));
  const tagColor = C.amber;
  const purchaseDateStr = dayjs(agent.createdAt).format("YYYY-MM-DD");

  const aifProgressPct =
    agent.aifDistributeDays > 0
      ? (agent.aifPaidDays / agent.aifDistributeDays) * 100
      : 0;

  return (
    <Dialog
      open={open}
      onOpenChange={v => {
        if (!v) onClose();
      }}
    >
      <DialogContent
        className="max-w-sm max-h-[85vh] overflow-y-auto mx-auto"
        style={{
          background: C.bg,
          border: `1px solid ${C.border}`,
          color: C.text,
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
              style={{
                background: `${C.primary}10`,
                border: `1px solid ${C.primary}20`,
              }}
            >
              <img
                src={AGENT_IMAGE_URL}
                alt="AI"
                className="w-6 h-6 object-contain"
                style={{ filter: "grayscale(1) brightness(0.5)" }}
              />
            </div>
            <div className="min-w-0 text-left">
              <div className="flex items-center gap-2 flex-wrap">
                <span style={{ color: C.primary }}>{agent.name}</span>
                <span
                  className="text-[9px] px-1.5 py-0.5 rounded font-semibold"
                  style={{
                    background: `${tagColor}12`,
                    color: tagColor,
                    border: `1px solid ${tagColor}20`,
                  }}
                >
                  {tierLabel}
                </span>
              </div>
              <div className="text-[10px]" style={{ color: C.dim }}>
                {t("homePage.myAgentInactiveDetailDialog.subtitle", {
                  date: purchaseDateStr,
                })}
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {/* <div className="flex items-center gap-2 flex-wrap">
            <span
              className="px-2.5 py-1 rounded text-[10px] font-semibold"
              style={{
                background: `${C.amber}10`,
                color: C.amber,
                border: `1px solid ${C.amber}20`,
              }}
            >
              {t("homePage.myAgentCard.statusPending")}
            </span>
            <span className="text-[10px]" style={{ color: C.dim }}>
              {t("homePage.myAgentInactiveDetailDialog.giftLevelShort", {
                level: agent.giftGiveLevel,
              })}
            </span>
          </div> */}

          <div className="grid grid-cols-4 gap-1.5">
            <div
              className="text-center py-2 rounded-lg"
              style={{
                background: `${C.primary}06`,
                border: `1px solid ${C.primary}10`,
              }}
            >
              <div className="text-[9px]" style={{ color: C.dim }}>
                {t("homePage.benefit.custodyBoost")}
              </div>
              <div
                className="text-xs font-bold mono-num truncate px-0.5"
                style={{ color: C.primary }}
              >
                {`${agent.aiManageAddSpeedBp / 100}%`}
              </div>
            </div>
            <div
              className="text-center py-2 rounded-lg"
              style={{
                background: `${C.green}06`,
                border: `1px solid ${C.green}10`,
              }}
            >
              <div className="text-[9px]" style={{ color: C.dim }}>
                {t("homePage.benefit.releaseMultiple")}
              </div>
              <div
                className="text-xs font-bold mono-num truncate px-0.5"
                style={{ color: C.green }}
              >
                {`${agent.exitMultiple / 100}x`}
              </div>
            </div>
            <div
              className="text-center py-2 rounded-lg"
              style={{
                background: `${C.amber}06`,
                border: `1px solid ${C.amber}10`,
              }}
            >
              <div className="text-[9px]" style={{ color: C.dim }}>
                {t("homePage.benefit.aifToken", {
                  tokenNameAif: getTokenName(CCC_AIF_ADDRESS),
                })}
              </div>
              <div
                className="text-xs font-bold mono-num truncate px-0.5"
                style={{ color: C.amber }}
              >
                {formatUsdPrefix(
                  formatThousands(
                    formatEtherWithDecimals(agent.aifValueUsdt, 0),
                    0
                  )
                )}
              </div>
            </div>
            {agent.giftGiveLevel > 0 ? (
              <div
                className="text-center py-2 rounded-lg"
                style={{
                  background: `${C.cyan}06`,
                  border: `1px solid ${C.cyan}10`,
                }}
              >
                <div className="text-[9px]" style={{ color: C.dim }}>
                  {t("homePage.benefit.teamLevel")}
                </div>
                <div
                  className="text-xs font-bold mono-num truncate px-0.5"
                  style={{ color: C.cyan }}
                >
                  {`C${agent.giftGiveLevel}`}
                </div>
              </div>
            ) : null}
          </div>

          <div
            className="rounded-lg p-3"
            style={{ background: C.card2, border: `1px solid ${C.border}` }}
          >
            <div
              className="text-[10px] font-semibold mb-2"
              style={{ color: C.muted }}
            >
              {t("homePage.myAgentInactiveDetailDialog.purchaseInfoTitle")}
            </div>
            <div className="flex justify-between text-sm mb-2">
              <span style={{ color: C.muted }}>
                {t("homePage.myAgentCard.purchasePrice")}
              </span>
              <span className="mono-num font-bold">
                {formatThousands(
                  formatEtherWithDecimals(agent.purchasePayUsdtTokenWei, 0),
                  0
                )}{" "}
                USDT
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span style={{ color: C.muted }}>
                {t("homePage.myAgentInactiveDetailDialog.exitCap")}
              </span>
              <span className="mono-num font-bold">
                {formatThousands(formatEtherWithDecimals(agent.staticCapUsdt))}{" "}
                USDT
              </span>
            </div>
            {hasFusdInPurchase && (
              <div
                className="flex items-center gap-1.5 mt-2 pt-2 text-[10px]"
                style={{
                  borderTop: `1px solid ${C.border}`,
                  color: mustPayFusdBeforeActivate ? C.red : C.green,
                }}
              >
                {mustPayFusdBeforeActivate ? (
                  <AlertCircle size={11} />
                ) : (
                  <CheckCircle2 size={11} />
                )}
                <span>
                  {mustPayFusdBeforeActivate
                    ? t(
                        "homePage.myAgentInactiveDetailDialog.pendingFusdNotice",
                        {
                          amount: formatThousands(
                            formatEtherWithDecimals(
                              agent.purchasePayFusdWei,
                              0
                            ),
                            0
                          ),
                          tokenName: getTokenName(CCC_FUSD_ADDRESS),
                          tokenNameUsdt: getTokenName(USDT_ADDRESS),
                        }
                      )
                    : t("homePage.myAgentInactiveDetailDialog.paidFusdNotice", {
                        amount: formatThousands(
                          formatEtherWithDecimals(agent.purchasePayFusdWei, 0),
                          0
                        ),
                        tokenName: getTokenName(CCC_FUSD_ADDRESS),
                        tokenNameUsdt: getTokenName(USDT_ADDRESS),
                      })}
                </span>
              </div>
            )}
          </div>

          <div
            className="rounded-lg p-3"
            style={{
              background: "rgba(245,158,11,0.04)",
              border: `1px solid ${C.amber}10`,
            }}
          >
            <div className="flex items-center gap-2 mb-2">
              <Flame size={12} style={{ color: C.amber }} />
              <span className="font-bold text-[11px]">
                {t("homePage.myAgentInactiveDetailDialog.aifSectionTitle", {
                  tokenNameAif: getTokenName(CCC_AIF_ADDRESS),
                })}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-2">
              <div>
                <span className="text-[9px]" style={{ color: C.dim }}>
                  {t("homePage.myAgentInactiveDetailDialog.aifTotalLabel")}
                </span>
                <div
                  className="text-xs font-bold mono-num"
                  style={{ color: C.amber }}
                >
                  {formatThousands(
                    formatEtherWithDecimals(agent.aifValueUsdt, 0),
                    0
                  )}{" "}
                  U
                </div>
              </div>
              <div>
                <span className="text-[9px]" style={{ color: C.dim }}>
                  {t("homePage.myAgentInactiveDetailDialog.aifReleasedLabel")}
                </span>
                <div className="text-xs font-bold mono-num">
                  {t("homePage.myAgentCard.aifDays", {
                    paid: agent.aifPaidDays,
                    total: agent.aifDistributeDays,
                  })}
                </div>
              </div>
              <div>
                <span className="text-[9px]" style={{ color: C.dim }}>
                  {t("homePage.myAgentInactiveDetailDialog.aifClaimedLabel")}
                </span>
                <div
                  className="text-xs font-bold mono-num"
                  style={{ color: C.primary }}
                >
                  ≈
                  {formatThousands(
                    formatEtherWithDecimals(agent.aifPaidValueUsdt, 0),
                    0
                  )}{" "}
                  U
                </div>
              </div>
            </div>
            <div
              className="h-1.5 rounded-full overflow-hidden"
              style={{ background: "rgba(255,255,255,0.04)" }}
            >
              <div
                className="h-full rounded-full"
                style={{
                  width: `${Math.min(100, aifProgressPct)}%`,
                  background: `linear-gradient(90deg, ${C.amber}, #fbbf24)`,
                }}
              />
            </div>
          </div>

          <div
            className="rounded-lg p-3"
            style={{ background: C.card2, border: `1px solid ${C.border}` }}
          >
            <div className="flex items-center gap-1.5 mb-2">
              <Package size={12} style={{ color: C.cyan }} />
              <span className="font-bold text-[11px]">
                {t("homePage.myAgentInactiveDetailDialog.hardwareTitle")}
              </span>
            </div>
            <div className="flex justify-between text-[11px]">
              <span style={{ color: C.dim }}>
                {t("homePage.myAgentCard.deliveryStatus")}
              </span>
              <span className="font-semibold" style={{ color: C.amber }}>
                {t("homePage.myAgentInactiveDetailDialog.hardwarePending")}
              </span>
            </div>
          </div>

          <div className="text-[10px] space-y-1 pt-1" style={{ color: C.dim }}>
            <div className="flex items-center gap-1">
              <Package size={9} />
              {t("homePage.agentDetailDialog.hardwareNote")}
            </div>
            <div className="flex items-center gap-1">
              <Clock size={9} />
              {t("homePage.myAgentInactiveDetailDialog.aifReleaseFootnote", {
                days: agent.aifDistributeDays,
                tokenNameAif: getTokenName(CCC_AIF_ADDRESS),
              })}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
